<template>
    <TinyEditor
        v-model="mutableValue"
        class="inserveEditor"
        license-key="gpl"
        :init="config"
        @drop="dropHandler($event);"
        @dragover="dragOverHandler($event);"
    />
</template>

<script>
import api from '@/services/Api';
import store from '@/store';
import TinyEditor from '@tinymce/tinymce-vue';

export default {
    components: { TinyEditor },
    props: {
        init: Object,
        modelValue: String,
        uploadToServer: {
            type: Boolean,
            default: true,
        },
        height: {
            type: Number,
            default: 310,
        },
        messageId: Number,
        publicAttachment: {
            type: Boolean,
            default: false
        },
        allowAttachmentsOtherThanImages: {
            type: Boolean,
            default: false,
        }
    },
    created () {
        if (this.init) {
            this.config = this.init;
        }
        this.mutableValue = this.modelValue;
    },
    data () {
        return {
            mutableValue: undefined,
            tinyKey: 1,
            config: {
                selector: '.inserveEditor',
                skin: 'oxide',
                content_css: 'default',
                license_key: 'gpl',
                promotion: false,
                branding: false,
                // Bust browser cache of the TinyMCE assets after upgrades
                cache_suffix: '?v=7.9.3',
                setup: (editor) => {
                    // XSS Mitigation: Strip event handlers from pasted content
                    editor.on('PastePreProcess', (e) => {
                        if (!e.content) {
                            return;
                        }

                        // Remove all event handler attributes (onclick, onload, onerror, etc.)
                        e.content = e.content.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
                        // Remove javascript: protocol from href and src
                        e.content = e.content.replace(/(href|src)\s*=\s*["']javascript:[^"']*["']/gi, '');
                    });

                    // Only allow mentioning operators on tickets
                    if (this.$router.currentRoute.value.name?.split('.')[0] !== 'tickets') {
                        return;
                    }

                    const onAction = function (autocompleteApi, rng, value) {
                        editor.selection.setRng(rng);
                        editor.insertContent(value);
                        autocompleteApi.hide();
                    };

                    const specialChars = store.getters.getSortedList('operators').map((operator) => {
                        return { text: operator.full_name, value: `<span class="mceNonEditable" style="font-weight: 500; color: #20a8d8;" data-mention-operator-id="${operator.id}">@${operator.full_name}</span> ` };
                    });

                    const getMatchedChars = function (pattern) {
                        pattern = pattern.toLowerCase();
                        return specialChars.filter((char) => {
                            return char.text.toLowerCase().indexOf(pattern) !== -1;
                        });
                    };

                    /* An autocompleter that allows you to insert special characters */
                    editor.ui.registry.addAutocompleter('specialchars', {
                        trigger: '@',
                        minChars: 1,
                        columns: 1,
                        onAction,
                        fetch (pattern) {
                            return new Promise((resolve) => {
                                const results = getMatchedChars(pattern).map((char) => {
                                    return {
                                        type: 'autocompleteitem',
                                        value: char.value,
                                        text: char.text,
                                    };
                                });
                                resolve(results);
                            });
                        },
                    });
                },
                plugins: 'code link image lists autolink',
                toolbar: `undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link ${this.uploadToServer ? 'image ' : ''} forecolor | removeformat code pastetext`,
                height: this.height,
                browser_spellcheck: true,
                contextmenu: false,
                block_unsupported_drop: !this.allowAttachmentsOtherThanImages,
                images_file_types: 'jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp',
                // We want to keep the base64 in some cases, so don't upload
                automatic_uploads: this.uploadToServer,
                images_upload_handler: (blob) => {
                    this.$emit('uploading');

                    const formData = new FormData();
                    formData.append('is_embedded', true);
                    formData.append('file', blob.blob(), blob.filename());

                    if (this.messageId) {
                        formData.append('message_id', this.messageId.toString());
                    }

                    if (this.publicAttachment) {
                        formData.append('is_public', true);
                    }

                    return api.post('/attachments', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then((response) => {
                        this.$emit('uploaded');
                        return response.data.url;
                    }).catch((error) => {
                        this.$emit('uploaded');
                        return Promise.reject(error);
                    });
                },
                menubar: false,
                language: this.$i18n.locale,
                paste_data_images: true,
                // XSS Mitigation: Block dangerous elements
                invalid_elements: 'script,iframe,object,embed,noscript',
                // XSS Mitigation: Block dangerous attributes that can execute JavaScript
                invalid_styles: 'expression',
                extended_valid_elements: 'contact-component[*],contact-telephone-component[*]',
                custom_elements: 'contact-component,contact-telephone-component',
                // XSS Mitigation: Sandbox any iframes that do get through
                sandbox_iframes: true,
                // XSS Mitigation: Convert unsafe embeds to safer alternatives
                convert_unsafe_embeds: true,
                relative_urls: false,
                content_style: 'p { margin: 0; }',
                convert_urls: false,
            }
        };
    },
    watch: {
        modelValue (val) {
            this.mutableValue = val;
        },
        mutableValue () {
            this.$emit('update:modelValue', this.mutableValue);
            this.$emit('input', this.mutableValue);
        },
    },
    methods: {
        dropHandler (ev) {
            if (!this.allowAttachmentsOtherThanImages) {
                return;
            }

            const supportedTinyMCETypes = this.config.images_file_types.split(',').map(type => 'image/' + type);

            ev.preventDefault();

            let attachments = [];

            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                [...ev.dataTransfer.items].forEach((item, i) => {
                    // If dropped items aren't images, add them as attachments instead.
                    if (!supportedTinyMCETypes.includes(item.type)) {
                        attachments.push(item.getAsFile());
                    }
                });
            } else {
                // Use DataTransfer interface to access the file(s)
                [...ev.dataTransfer.files].forEach((file, i) => {
                    // If dropped items aren't images, add them as attachments instead.
                    if (!supportedTinyMCETypes.includes(file.type)) {
                        attachments.push(file);
                    }
                });
            }

            this.$emit('upload-attachments', attachments)
        },
        dragOverHandler (ev) {
            if (!this.allowAttachmentsOtherThanImages) {
                return;
            }

            // Prevent default behavior (Prevent file from being opened)
            ev.preventDefault();
        }
    }
};
</script>
